'use client'

import { MouseEvent } from 'react'

import { toast } from 'sonner'

import {
  useAutoComplete,
  type AutoCompletableType
} from '@/shared/hooks/useAutoComplete'
import { useIsAdmin } from '@/shared/hooks/useIsAdmin'
import { describeAutoCompleteBlock } from '@/shared/lib/describeAutoCompleteBlock'
import { cn } from '@/shared/lib/utils'

interface AdminAutoCompleteButtonProps {
  type: AutoCompletableType
  id: string | number
  label?: string
  className?: string
}

/**
 * Botão de auto-conclusão, visível apenas para administradores.
 *
 * Existe para o suporte alcançar qualquer conteúdo sem alterar a mecânica que o
 * aluno enfrenta: a conclusão passa pelo mesmo pipeline dele, então o app segue
 * se comportando como se comportaria para o aluno — que é a condição para
 * reproduzir o erro relatado.
 *
 * A casca só resolve o papel; os hooks de progresso ficam no componente interno,
 * montado apenas para admin. Assim um card de conteúdo renderizado fora dos
 * providers (nos testes, por exemplo) não paga por um afford que nem apareceria.
 */
export const AdminAutoCompleteButton = (props: AdminAutoCompleteButtonProps) => {
  const isAdmin = useIsAdmin()

  if (!isAdmin) return null

  return <AutoCompleteAction {...props} />
}

const AutoCompleteAction = ({
  type,
  id,
  label = 'Auto-concluir',
  className
}: AdminAutoCompleteButtonProps) => {
  const { autoComplete, isRunning, status } = useAutoComplete()

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    // O botão costuma ficar dentro de um card clicável — sem isto, concluir
    // também navegaria para o conteúdo.
    event.preventDefault()
    event.stopPropagation()

    try {
      const result = await autoComplete({ type, id })

      if (!result) return

      if (result.status === 'failed') {
        // A API já grava a mensagem em linguagem de gente; repassar direto evita
        // sobrepor o motivo real com um texto genérico.
        toast.error(
          result.error_message ?? 'A auto-conclusão falhou durante o processamento'
        )
        return
      }

      if (result.blocked.length > 0) {
        toast.warning(
          `Bloqueios não contornados: ${result.blocked
            .map(describeAutoCompleteBlock)
            .join('; ')}`
        )
        return
      }

      toast.success(`${result.completed.length} item(ns) concluído(s)`)
    } catch {
      toast.error('Não foi possível auto-concluir este conteúdo')
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isRunning}
      data-testid="admin-auto-complete"
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-amber-400/60 bg-amber-500/10 px-2 py-1',
        'text-[11px] font-bold uppercase tracking-wide text-amber-600 disabled:opacity-50',
        className
      )}
    >
      {isRunning
        ? status === 'processing'
          ? 'Processando…'
          : 'Na fila…'
        : label}
    </button>
  )
}
